
import { useState, useEffect } from "react";
import { useProvider } from "@/hooks/useProviders";
import { useProviderBookings, useProviderBookingStats } from "@/hooks/useProviderBookings";
import { useRooms } from "@/hooks/useRooms";
import { useServices } from "@/hooks/useServices";
import { useFacilities } from "@/hooks/useFacilities";
import { useMemberships, useDeleteMembership } from "@/hooks/useMemberships";
import { useCoupons } from "@/hooks/useCoupons";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useDevices, Device } from "@/hooks/useDevices";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate, Link } from "react-router-dom";
import ProviderHeader from "@/components/ProviderHeader";
import ProviderStatsCards from "@/components/provider/ProviderStatsCards";
import ProviderDashboardNavigation from "@/components/provider/ProviderDashboardNavigation";
import BookingsManagement from "@/components/provider/BookingsManagement";
import RoomsManagement from "@/components/provider/RoomsManagement";
import DevicesManagement from "@/components/provider/DevicesManagement";
import FacilitiesManagement from "@/components/provider/FacilitiesManagement";
import MembershipsManagement from "@/components/provider/MembershipsManagement";
import PromotionsManagement from "@/components/provider/PromotionsManagement";
import CouponsManagementSection from "@/components/provider/CouponsManagementSection";
import PenaltiesManagement from "@/components/provider/PenaltiesManagement";
import TimeSlotManagement from "@/components/provider/TimeSlotManagement";
import ProviderSettings from "@/components/provider/ProviderSettings";
import ProviderReviewsTab from "@/components/provider/ProviderReviewsTab";
import WaiverManagement from "@/components/provider/WaiverManagement";
import CustomerManagement from "@/components/provider/CustomerManagement";
import PurchasesManagement from "@/components/provider/PurchasesManagement";
import GalleryManagement from "@/components/provider/GalleryManagement";
import CreateBookingDialog from "@/components/provider/CreateBookingDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, Star, User, Settings, Images, AlertTriangle, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const { data: providerAccess, isLoading: accessLoading } = useProviderAccess();
  
  // Get provider data based on user's provider assignment
  const { data: provider, isLoading: providerLoading, refetch: refetchProvider } = useQuery({
    queryKey: ['provider-by-access', providerAccess?.provider_id],
    queryFn: async () => {
      if (!providerAccess?.provider_id) return null;
      
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('id', providerAccess.provider_id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!providerAccess?.provider_id,
  });

  const { data: bookings, isLoading: bookingsLoading, refetch: refetchBookings, error: bookingsError } = useProviderBookings(provider?.id);
  const { data: stats, isLoading: statsLoading } = useProviderBookingStats(provider?.id);
  const { data: rooms, isLoading: roomsLoading, refetch: refetchRooms } = useRooms(provider?.id);
  const { data: services, isLoading: servicesLoading, refetch: refetchServices } = useServices(provider?.id);
  const { data: facilities, isLoading: facilitiesLoading, refetch: refetchFacilities } = useFacilities(provider?.id || '');
  const { data: memberships, isLoading: membershipsLoading, refetch: refetchMemberships } = useMemberships(provider?.id);
  const { data: coupons, isLoading: couponsLoading, refetch: refetchCoupons } = useCoupons(provider?.id);
  const { data: devices, isLoading: devicesLoading, refetch: refetchDevices } = useDevices(provider?.id);
  const deleteMembership = useDeleteMembership();
  const [activeSection, setActiveSection] = useState("bookings");
  const [createBookingDialogOpen, setCreateBookingDialogOpen] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    console.log('ProviderDashboard mounted');
    console.log('Active section:', activeSection);
    console.log('Provider access:', providerAccess);
    console.log('Provider data:', provider);
    console.log('Bookings data:', bookings);
    console.log('Bookings loading:', bookingsLoading);
    console.log('Bookings error:', bookingsError);
  }, [activeSection, providerAccess, provider, bookings, bookingsLoading, bookingsError]);

  const handleStatusUpdate = async (bookingId: string, status: string) => {
    try {
      // Get current user's email
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email || 'unknown@provider.com';

      // Get current booking to append to existing notes
      const { data: currentBooking, error: fetchError } = await supabase
        .from('bookings')
        .select('notes')
        .eq('id', bookingId)
        .single();

      if (fetchError) throw fetchError;

      // Get current date and time for the audit trail
      const currentDateTime = format(new Date(), 'MMM dd, yyyy HH:mm:ss');

      // Create the status update note with date/time
      const statusNote = status === 'completed' 
        ? `Manually marked as completed by ${userEmail} on ${currentDateTime}`
        : `Status changed to ${status} by ${userEmail} on ${currentDateTime}`;

      // Combine existing notes with new status note
      const existingNotes = currentBooking?.notes || '';
      const updatedNotes = existingNotes 
        ? `${existingNotes} | ${statusNote}`
        : statusNote;

      const { error } = await supabase
        .from('bookings')
        .update({ 
          status, 
          notes: updatedNotes,
          updated_at: new Date().toISOString() 
        })
        .eq('id', bookingId);

      if (error) throw error;

      toast({
        title: "Booking Updated",
        description: `Booking status changed to ${status}`,
      });
      
      refetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: "Error",
        description: "Failed to update booking status",
        variant: "destructive",
      });
    }
  };

  const handleRoomAdded = () => {
    refetchRooms();
  };

  const handleServiceAdded = () => {
    refetchServices();
  };

  const handleServicesUpdated = () => {
    refetchServices();
  };

  const handleProviderUpdate = () => {
    refetchProvider();
  };

  const handleFacilityUpdated = () => {
    refetchFacilities();
  };

  const handleReviewAdded = () => {
    refetchBookings();
  };

  const handleMembershipUpdate = () => {
    refetchMemberships();
  };

  const handleMembershipDelete = async (membershipId: string) => {
    console.log('Deleting membership:', membershipId);
    try {
      await deleteMembership.mutateAsync(membershipId);
      refetchMemberships();
    } catch (error) {
      console.error('Error deleting membership:', error);
    }
  };

  const handleCouponUpdate = () => {
    refetchCoupons();
  };

  const handleDeviceUpdated = () => {
    refetchDevices();
  };

  const getServicesCountForRoom = (roomId: string) => {
    return services?.filter(service => service.room_id === roomId).length || 0;
  };

  const handleViewBookingDetails = (bookingId: string) => {
    console.log('Navigating to booking detail with ID:', bookingId);
    navigate(`/management/booking/${bookingId}`);
  };

  const handleSectionChange = (section: string) => {
    if (section === "qr-generator") {
      // Navigate to window sticker page with provider ID as query parameter
      navigate(`/window-sticker?providerId=${provider?.id}`);
      return;
    }
    setActiveSection(section);
  };

  const handleBookingCreated = () => {
    refetchBookings();
    // Also refresh time slots data
    window.location.reload(); // Simple refresh to update all cached data
  };

  const renderContent = () => {
    console.log('renderContent called with activeSection:', activeSection);
    
    switch (activeSection) {
      case "customers":
        return <CustomerManagement providerId={provider?.id || ''} />;

      case "bookings":
        return (
          <BookingsManagement
            bookings={bookings || []}
            bookingsLoading={bookingsLoading}
            bookingsError={bookingsError}
            onViewDetails={handleViewBookingDetails}
            onStatusUpdate={handleStatusUpdate}
          />
        );

      case "purchases":
        return <PurchasesManagement providerId={provider?.id || ''} />;

      case "rooms":
        return (
          <RoomsManagement
            providerId={provider?.id || ''}
            rooms={rooms || []}
            roomsLoading={roomsLoading}
            servicesCount={getServicesCountForRoom}
            onRoomAdded={handleRoomAdded}
            onServicesUpdated={handleServicesUpdated}
          />
        );

      case "devices":
        return (
          <DevicesManagement
            providerId={provider?.id || ''}
            devices={devices || []}
            devicesLoading={devicesLoading}
            rooms={rooms || []}
            onDeviceAdded={handleDeviceUpdated}
          />
        );

      case "facilities":
        return (
          <FacilitiesManagement
            providerId={provider?.id || ''}
            facilities={facilities || []}
            facilitiesLoading={facilitiesLoading}
            onFacilityUpdated={handleFacilityUpdated}
          />
        );

      case "gallery":
        return <GalleryManagement providerId={provider?.id || ''} />;

      case "memberships":
        return (
          <MembershipsManagement
            providerId={provider?.id || ''}
            memberships={memberships || []}
            membershipsLoading={membershipsLoading}
            services={services || []}
            onMembershipUpdate={handleMembershipUpdate}
            onMembershipDelete={handleMembershipDelete}
          />
        );

          case 'promotions':
            return <PromotionsManagement providerId={provider?.id || ''} services={services || []} />;

          case 'coupons':
        return (
          <CouponsManagementSection
            providerId={provider?.id || ''}
            services={services || []}
            onCouponUpdate={handleCouponUpdate}
          />
        );

      case "penalties":
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" />
                <span>Penalties Management</span>
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <PenaltiesManagement providerId={provider?.id || ''} />
            </CardContent>
          </Card>
        );

      case "time-slots":
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Time Slot Management</span>
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <TimeSlotManagement 
                providerId={provider?.id || ''} 
                services={services || []}
                rooms={rooms || []}
              />
            </CardContent>
          </Card>
        );

      case "reviews":
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Star className="h-5 w-5" />
                <span>Reviews & Ratings</span>
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <ProviderReviewsTab providerId={provider?.id || ''} />
            </CardContent>
          </Card>
        );

      case "waivers":
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Waiver Management</span>
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <WaiverManagement providerId={provider?.id || ''} />
            </CardContent>
          </Card>
        );

      case "settings":
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Provider Settings</span>
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <ProviderSettings 
                provider={provider} 
                onProviderUpdate={handleProviderUpdate}
              />
            </CardContent>
          </Card>
        );

      default:
        console.log('Rendering default/coming soon section for:', activeSection);
        return (
          <Card>
            <CardContent className="p-8 text-center">
              <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
              <p className="text-gray-600">This section is under development.</p>
            </CardContent>
          </Card>
        );
    }
  };

  if (accessLoading || providerLoading) {
    return (
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Inter, sans-serif' }}>
        <ProviderHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!providerAccess || !provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProviderHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
            <p className="text-gray-600 mt-2">You don't have access to any provider dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      <ProviderHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{provider?.name} Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your bookings and availability</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Button 
              variant="outline"
              className="w-full sm:w-auto"
              asChild
            >
              <Link to={`/provider/${provider?.slug}`}>
                <Eye className="h-4 w-4 mr-2" />
                View Profile
              </Link>
            </Button>
            <Button 
              className="w-full sm:w-auto"
              onClick={() => setCreateBookingDialogOpen(true)}
            >
              <Calendar className="h-4 w-4 mr-2" />
              New Booking
            </Button>
          </div>
        </div>

        {/* Navigation and Content */}
        <div className="space-y-6">
          {/* Navigation Bar - Moved above stats */}
          <ProviderDashboardNavigation
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />

          {/* Stats Cards - Now below navigation */}
          <ProviderStatsCards stats={stats} isLoading={statsLoading} />

          {/* Main Content */}
          {renderContent()}
        </div>
      </div>

      {/* Create Booking Dialog */}
      <CreateBookingDialog
        open={createBookingDialogOpen}
        onOpenChange={setCreateBookingDialogOpen}
        providerId={provider?.id || ''}
        onBookingCreated={handleBookingCreated}
      />
    </div>
  );
};

export default ProviderDashboard;
